import { getGeonameIdByResortName } from '@/constants/resort-geonames.constant';
import { useCheckoutHydration } from '@/hooks/useCheckoutHydration';
import type { Domain, DomainSelectItem } from '@/services/inventory-api.service';
import { inventoryApi, isDomainInactive } from '@/services/inventory-api.service';
import {
  getCheckoutSession,
  ordersService,
  setCheckoutSession,
  type CreateOrderDraftPayload,
} from '@/services/orders.service';
import { useBookSkiPassStore, type SkierCounts } from '@/store/book-ski-pass.store';
import { formatLocalDateOnly, parseDateOnly } from '@/utils/date-only';
import { getAllowedSkierKeysFromRestrictions } from '@/utils/domain-restrictions';
import {
  analyzeReferenceDomainActivity,
  filterResortDomainEntriesToActive,
  pruneActiveDomainMap,
} from '@/utils/reference-domain-activity';
import { buildSkierCountOffersFetchKey } from '@/utils/offers-fetch-key';
import { fetchDomainOffersEntries } from '@/utils/fetch-domain-offers-entries';
import { setSkierCountOffersPrefetch } from '@/utils/skier-count-offers-prefetch';
import {
  buildTripCalculatePeople,
  getDomainsToFetchIds,
} from '@/utils/trip-offers-calculate';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const getValidStartingDate = (
  startDate: string,
  minDate: Date,
  hasReferenceDate: boolean
): string | null => {
  if (hasReferenceDate) return null;
  const current = parseDateOnly(startDate);
  if (!current) return null;
  const min = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
  const cur = new Date(current.getFullYear(), current.getMonth(), current.getDate());
  return cur < min ? formatLocalDateOnly(min) : null;
};

export const useTripDetailsForm = () => {
  const navigate = useNavigate();
  const stepTwoFormFieldsValues = useBookSkiPassStore((state) => state.stepTwoFormFieldsValues);
  const stepOneFormFieldsValues = useBookSkiPassStore((state) => state.stepOneFormFieldsValues);
  const updateStepTwoFormFields = useBookSkiPassStore((state) => state.updateStepTwoFormFields);
  const referenceResponseData = useBookSkiPassStore((state) => state.referenceResponseData);
  const hasAppliedReferenceToStepTwo = useBookSkiPassStore(
    (state) => state.hasAppliedReferenceToStepTwo
  );
  const setHasAppliedReferenceToStepTwo = useBookSkiPassStore(
    (state) => state.setHasAppliedReferenceToStepTwo
  );
  const bookingReferenceNumber = useBookSkiPassStore((state) => state.bookingReferenceNumber);
  const selectedDomain = useBookSkiPassStore((state) => state.selectedDomain);
  const setSelectedDomain = useBookSkiPassStore((state) => state.setSelectedDomain);
  const resorts = useBookSkiPassStore((state) => state.resorts);
  const setResorts = useBookSkiPassStore((state) => state.setResorts);
  const referenceResortDomains = useBookSkiPassStore((state) => state.referenceResortDomains);
  const setReferenceResortDomains = useBookSkiPassStore((state) => state.setReferenceResortDomains);
  const purgeReferenceDomainIds = useBookSkiPassStore((state) => state.purgeReferenceDomainIds);
  const setReferenceResponseData = useBookSkiPassStore((state) => state.setReferenceResponseData);
  const setLoading = useBookSkiPassStore((state) => state.setLoading);
  const setError = useBookSkiPassStore((state) => state.setError);
  const clearOffersDependentSelections = useBookSkiPassStore(
    (state) => state.clearOffersDependentSelections
  );
  const manualSkiAreaSelection = useBookSkiPassStore((state) => state.manualSkiAreaSelection);
  const setManualSkiAreaSelection = useBookSkiPassStore((state) => state.setManualSkiAreaSelection);

  const [needsDomainPreStep, setNeedsDomainPreStep] = useState(false);
  const [ctaInlineMessage, setCtaInlineMessage] = useState<'noOffers' | 'technical' | null>(null);
  const hasFetchedDomain = useRef(false);
  const previousResortName = useRef<string | undefined>(undefined);

  useCheckoutHydration({
    hydrateTripDetails: true,
    onError: setError,
  });

  const bookingReference = bookingReferenceNumber || '';
  const resortName = referenceResponseData?.resort;

  const buildSkierCounts = useCallback((counts: SkierCounts) => {
    return Object.entries(counts)
      .map(([category, quantity]) => ({ category, quantity }))
      .filter(({ quantity }) => quantity > 0);
  }, []);

  const fetchFullDomain = useCallback(
    async (domainPk: number) => {
      const fullDomain = await inventoryApi.getDomainById(domainPk);
      setSelectedDomain(fullDomain);
    },
    [setSelectedDomain]
  );

  const fetchDomainByGeoname = useCallback(async () => {
    let refData = useBookSkiPassStore.getState().referenceResponseData;
    let refActiveById = new Map<number, Domain>();

    if (refData) {
      const { inactiveIds, activeById } = await analyzeReferenceDomainActivity(refData);
      refActiveById = activeById;
      if (inactiveIds.length > 0) {
        purgeReferenceDomainIds(inactiveIds);
        refData = useBookSkiPassStore.getState().referenceResponseData;
        pruneActiveDomainMap(refActiveById, refData);
      }
    }

    const resolvedName = (refData?.resort ?? '').trim();
    const geonameId = refData?.geonamesId ?? resolvedName ? getGeonameIdByResortName(resolvedName) : null;

    if (refData?.priority_domain && refData.domains && refData.domains.length > 0) {
      setReferenceResortDomains(refData.domains);
      if (!useBookSkiPassStore.getState().manualSkiAreaSelection) {
        const pid = refData.priority_domain.id;
        const fullDomain =
          refActiveById.get(pid) ?? (await inventoryApi.getDomainById(pid, true));
        if (isDomainInactive(fullDomain)) {
          purgeReferenceDomainIds([pid]);
          return false;
        }
        setSelectedDomain(fullDomain);
        useBookSkiPassStore.getState().captureReferenceDomainSeasonSnapshot(fullDomain);
      }
      if (geonameId) {
        const resortsData = await inventoryApi.searchResorts(geonameId);
        setResorts(resortsData);
      }
      return Boolean(useBookSkiPassStore.getState().selectedDomain);
    }

    if (!geonameId) return false;

    const resortsData = await inventoryApi.searchResorts(geonameId);
    setResorts(resortsData);

    const firstResortWithDomain = resortsData.find((resort) => resort.priority_domain);
    if (!firstResortWithDomain?.priority_domain) return false;

    const { activeEntries, inactiveIds, activeById } = await filterResortDomainEntriesToActive(
      firstResortWithDomain.domains,
      firstResortWithDomain.priority_domain
    );
    if (inactiveIds.length > 0) {
      purgeReferenceDomainIds(inactiveIds);
    }
    setReferenceResortDomains(activeEntries.length > 0 ? activeEntries : null);

    const currentRefData = useBookSkiPassStore.getState().referenceResponseData;
    if (currentRefData && !currentRefData.domains?.length) {
      const priorityMeta = firstResortWithDomain.priority_domain;
      const priorityOk = activeById.has(priorityMeta.id);
      setReferenceResponseData({
        ...currentRefData,
        domains: activeEntries,
        priority_domain: priorityOk ? priorityMeta : undefined,
      });
    }

    if (useBookSkiPassStore.getState().manualSkiAreaSelection) {
      return Boolean(useBookSkiPassStore.getState().selectedDomain);
    }

    const resortPriority = firstResortWithDomain.priority_domain;
    const fullDomainFromResort = activeById.get(resortPriority.id);
    if (!fullDomainFromResort) {
      return false;
    }
    setSelectedDomain(fullDomainFromResort);
    useBookSkiPassStore.getState().captureReferenceDomainSeasonSnapshot(fullDomainFromResort);
    return true;
  }, [
    purgeReferenceDomainIds,
    setReferenceResponseData,
    setResorts,
    setReferenceResortDomains,
    setSelectedDomain,
  ]);

  useEffect(() => {
    if (!referenceResponseData || hasAppliedReferenceToStepTwo) {
      return;
    }

    setHasAppliedReferenceToStepTwo(true);

    const referenceDate = parseDateOnly(referenceResponseData.date);
    if (!referenceDate) {
      return;
    }
    const datePlusOne = new Date(referenceDate);
    datePlusOne.setDate(datePlusOne.getDate() + 1);
    const formattedDatePlusOne = formatLocalDateOnly(datePlusOne);

    const adjustedDuration = referenceResponseData.duration - 1;
    updateStepTwoFormFields({
      daysOfSkiing: adjustedDuration > 0 ? String(adjustedDuration) : '1',
      startingDate: formattedDatePlusOne,
    });
  }, [
    referenceResponseData,
    hasAppliedReferenceToStepTwo,
    setHasAppliedReferenceToStepTwo,
    updateStepTwoFormFields,
  ]);

  useEffect(() => {
    if (resortName !== previousResortName.current) {
      previousResortName.current = resortName;
      hasFetchedDomain.current = false;
      setNeedsDomainPreStep(false);
    }

    const shouldRestoreReferenceDomains =
      Boolean(resortName) &&
      !useBookSkiPassStore.getState().manualSkiAreaSelection &&
      Boolean(referenceResponseData) &&
      !(referenceResortDomains?.length ?? 0);

    if (
      (selectedDomain ||
        useBookSkiPassStore.getState().manualSkiAreaSelection ||
        hasFetchedDomain.current) &&
      !shouldRestoreReferenceDomains
    ) {
      return;
    }

    hasFetchedDomain.current = true;

    const loadDomain = async () => {
      setLoading(true);
      setError(null);

      try {
        const foundViaGeoname = resortName ? await fetchDomainByGeoname() : false;
        if (!foundViaGeoname && !useBookSkiPassStore.getState().selectedDomain) {
          setNeedsDomainPreStep(true);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch domain';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void loadDomain();
  }, [
    selectedDomain,
    resortName,
    referenceResponseData,
    referenceResortDomains,
    fetchDomainByGeoname,
    setError,
    setLoading,
  ]);

  const minDate = useMemo(() => {
    const today = new Date();
    const todayPlus2Days = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    todayPlus2Days.setDate(todayPlus2Days.getDate() + 2);
    return todayPlus2Days;
  }, []);

  useEffect(() => {
    const validDate = getValidStartingDate(
      stepTwoFormFieldsValues.startingDate,
      minDate,
      Boolean(referenceResponseData?.date)
    );
    if (validDate) updateStepTwoFormFields({ startingDate: validDate });
  }, [
    referenceResponseData,
    minDate,
    stepTwoFormFieldsValues.startingDate,
    updateStepTwoFormFields,
  ]);

  const skierCountsKey = useMemo(
    () => JSON.stringify(stepTwoFormFieldsValues.skierCounts),
    [stepTwoFormFieldsValues.skierCounts]
  );

  useEffect(() => {
    setCtaInlineMessage(null);
  }, [stepTwoFormFieldsValues.daysOfSkiing, stepTwoFormFieldsValues.startingDate, skierCountsKey]);

  useEffect(() => {
    const allowedKeys = getAllowedSkierKeysFromRestrictions(selectedDomain?.restrictions);
    if (!allowedKeys?.length) return;

    const counts = stepTwoFormFieldsValues.skierCounts;
    const allowedSet = new Set(allowedKeys);
    const hasDisallowed = Object.keys(counts).some(
      (key) => (counts[key] ?? 0) > 0 && !allowedSet.has(key)
    );
    if (!hasDisallowed) return;

    const next: SkierCounts = {};
    for (const key of allowedKeys) {
      next[key] = counts[key] ?? 0;
    }
    updateStepTwoFormFields({ skierCounts: next });
  }, [
    selectedDomain?.pk,
    selectedDomain?.restrictions,
    stepTwoFormFieldsValues.skierCounts,
    updateStepTwoFormFields,
  ]);

  const totalSkiers = useMemo(() => {
    const counts = stepTwoFormFieldsValues.skierCounts;
    return Object.values(counts).reduce((sum, c) => sum + c, 0);
  }, [stepTwoFormFieldsValues.skierCounts]);

  const isRequiredFieldsFilled = useMemo(() => {
    return Boolean(
      totalSkiers > 0 &&
      stepTwoFormFieldsValues.daysOfSkiing &&
      stepTwoFormFieldsValues.startingDate
    );
  }, [totalSkiers, stepTwoFormFieldsValues.daysOfSkiing, stepTwoFormFieldsValues.startingDate]);

  const originalConnectedDomains = useMemo(() => {
    if (referenceResponseData?.domains?.length) return referenceResponseData.domains;
    const resortWithDomain = resorts.find((r) => r.priority_domain);
    return resortWithDomain?.domains ?? [];
  }, [referenceResponseData?.domains, resorts]);

  const isDomainConnected = useMemo(() => {
    if (!selectedDomain || !manualSkiAreaSelection) return true;
    if (originalConnectedDomains.length === 0) return true;
    return originalConnectedDomains.some((d) => d.id === selectedDomain.pk);
  }, [selectedDomain, manualSkiAreaSelection, originalConnectedDomains]);

  const isSubmitDisabled = useMemo(() => {
    return !isRequiredFieldsFilled;
  }, [isRequiredFieldsFilled]);

  const handleSkiersLevelSelect = useCallback(
    (level: 'beginner' | 'intermediate' | 'advanced') => {
      updateStepTwoFormFields({ skiersLevel: level });
    },
    [updateStepTwoFormFields]
  );

  const handleSkiStyleSelect = useCallback(
    (style: 'fullDays' | 'balancedPace' | 'relaxed') => {
      updateStepTwoFormFields({ skiStyle: style });
    },
    [updateStepTwoFormFields]
  );

  const handleSkierCountsChange = useCallback(
    (counts: SkierCounts) => {
      const prevCounts = stepTwoFormFieldsValues.skierCounts;
      const keys = new Set([...Object.keys(prevCounts), ...Object.keys(counts)]);
      const changed = Array.from(keys).some((key) => (prevCounts[key] ?? 0) !== (counts[key] ?? 0));
      if (changed) {
        clearOffersDependentSelections();
      }
      updateStepTwoFormFields({ skierCounts: counts });
    },
    [clearOffersDependentSelections, stepTwoFormFieldsValues.skierCounts, updateStepTwoFormFields]
  );

  const handleDaysChange = useCallback(
    (value: string) => {
      updateStepTwoFormFields({ daysOfSkiing: value });
    },
    [updateStepTwoFormFields]
  );

  const handleDateChange = useCallback(
    (value: string) => {
      updateStepTwoFormFields({ startingDate: value });
    },
    [updateStepTwoFormFields]
  );

  const handleDomainChange = useCallback(
    (domain: DomainSelectItem) => {
      if (selectedDomain?.pk !== domain.id) {
        clearOffersDependentSelections();
      }
      setManualSkiAreaSelection(true);
      void fetchFullDomain(domain.id);
    },
    [clearOffersDependentSelections, fetchFullDomain, selectedDomain?.pk, setManualSkiAreaSelection]
  );

  const handlePreStepDomainConfirm = useCallback(
    (domain: DomainSelectItem) => {
      setNeedsDomainPreStep(false);
      setManualSkiAreaSelection(true);
      void fetchFullDomain(domain.id);
    },
    [fetchFullDomain, setManualSkiAreaSelection]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (isSubmitDisabled) {
        return;
      }

      const skiDays = Number.parseInt(stepTwoFormFieldsValues.daysOfSkiing, 10);
      if (!stepTwoFormFieldsValues.startingDate || Number.isNaN(skiDays)) {
        setError('Invalid trip details');
        return;
      }

      const reference = bookingReferenceNumber || stepOneFormFieldsValues.bookingReference;

      if (useBookSkiPassStore.getState().manualSkiAreaSelection && selectedDomain) {
        const selectedEntry = { id: selectedDomain.pk, name: selectedDomain.title };
        const storeState = useBookSkiPassStore.getState();
        let originalDomains = storeState.referenceResponseData?.domains;
        if (!originalDomains?.length) {
          const resortWithDomain = storeState.resorts.find((r) => r.priority_domain);
          originalDomains = resortWithDomain?.domains;
        }
        const otherDomains = (originalDomains ?? []).filter((d) => d.id !== selectedDomain.pk);
        setReferenceResortDomains([selectedEntry, ...otherDomains]);
      } else if (referenceResponseData?.domains?.length) {
        setReferenceResortDomains(referenceResponseData.domains);
      }

      const payload: CreateOrderDraftPayload = {
        skierCounts: buildSkierCounts(stepTwoFormFieldsValues.skierCounts),
        skiDays,
        startDate: stepTwoFormFieldsValues.startingDate,
        domain: selectedDomain ? { id: selectedDomain.pk, name: selectedDomain.title } : undefined,
        promoCode: stepOneFormFieldsValues.promoCode || undefined,
        reference: reference ? reference.trim() : undefined,
        skiersLevel: stepTwoFormFieldsValues.skiersLevel ?? undefined,
        skiStyle: stepTwoFormFieldsValues.skiStyle ?? undefined,
      };

      const submitDraft = async () => {
        setLoading(true);
        setCtaInlineMessage(null);
        setError(null);
        try {
          const session = getCheckoutSession();
          const response = session
            ? await ordersService.updateTripDetails(session.orderId, session.checkoutToken, payload)
            : await ordersService.createDraft(payload);
          setCheckoutSession({
            orderId: response.orderId,
            checkoutToken: response.checkoutToken,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create order draft';
          setError(message);
          setLoading(false);
          return;
        }

        try {
          const { selectedDomain: tripDomain, referenceResortDomains: tripResortDomains } =
            useBookSkiPassStore.getState();
          const domainIds = getDomainsToFetchIds(tripDomain, tripResortDomains);
          const startDate = stepTwoFormFieldsValues.startingDate;
          const people = buildTripCalculatePeople(
            stepTwoFormFieldsValues.skierCounts,
            startDate,
            skiDays,
            tripDomain?.restrictions
          );

          if (domainIds.length === 0 || people.length === 0) {
            setCtaInlineMessage('technical');
            return;
          }

          const entries = await fetchDomainOffersEntries(domainIds, people);
          const offerCount = entries.reduce((n, e) => n + e.offers.items.length, 0);
          console.log('Fetched offers entries:', entries);
          //console.log('Total offer count:', offerCount);
          if (offerCount > 0) {
            const prefetchKey = buildSkierCountOffersFetchKey(
              domainIds,
              stepTwoFormFieldsValues,
              people
            );
            setSkierCountOffersPrefetch({ key: prefetchKey, entries });
            void navigate('/ski-pass-offers');
          } else {
            setCtaInlineMessage('noOffers');
          }
        } catch {
          setCtaInlineMessage('technical');
        } finally {
          setLoading(false);
        }
      };

      void submitDraft();
    },
    [
      buildSkierCounts,
      bookingReferenceNumber,
      isSubmitDisabled,
      navigate,
      referenceResponseData,
      selectedDomain,
      setError,
      setLoading,
      setReferenceResortDomains,
      stepOneFormFieldsValues,
      stepTwoFormFieldsValues,
    ]
  );

  return {
    stepTwoFormFieldsValues,
    bookingReference,
    resortName,
    isDomainConnected,
    isSubmitDisabled,
    needsDomainPreStep,
    minDate,
    handleSkiersLevelSelect,
    handleSkiStyleSelect,
    handleSkierCountsChange,
    handleDaysChange,
    handleDateChange,
    handleDomainChange,
    handlePreStepDomainConfirm,
    handleSubmit,
    ctaInlineMessage,
  };
};
